import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Spin, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { message } from '../../services/toastMessage';
import { getMyAssistantGuide, updateMyAssistantGuide } from '../../services/ServiceProviderProfileService';
import {
  ASSISTANT_GUIDE_MAX_QUESTIONS,
  ASSISTANT_GUIDE_MAX_QUESTION_LENGTH,
} from '../../interfaces/heavyDutyMachinery/IAssistantGuide';
import { TRANSLATIONS } from '../../constants/translations';
import { translate } from '../../helpers/translationHelper';
import { TEAL, TEAL_SOFT_BG } from '../../theme/creamyTokens';

const { Text, Paragraph } = Typography;

/** A stable key per row, so removing the third one does not move what is in the fourth. */
interface GuideRow {
  key: string;
  text: string;
}

/**
 * "Lo que necesito saber antes de dar un precio" -- the professional's own
 * questions, in their own words.
 *
 * The one design decision that matters here: **it is never a blank box.**
 * Put an empty field in front of somebody labelled "write down what you need
 * to know before you can quote" and nobody fills it in -- not a few, nobody.
 * Show them five sensible questions and they delete two, reword one and add
 * the thing they always end up ringing people about, and that takes thirty
 * seconds and actually happens. The server does its half of that by never
 * returning an empty list on a first read (see getMyAssistantGuide).
 *
 * What they write here does not become a form the customer fills in. The
 * assistant rewords each one for whoever is answering, skips the ones the
 * description already covers, and still only ever asks three -- the customer
 * walks away from the sixth question no matter who wrote it. Said on screen,
 * because a professional who expects all eight to be asked would be right to
 * feel lied to.
 */
const AssistantGuideEditor: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();

  const [rows, setRows] = useState<GuideRow[]>([]);
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // No provider profile yet (or services switched off here). Nothing to edit,
  // and nothing anybody did wrong -- so the block puts itself away.
  const [unavailable, setUnavailable] = useState(false);

  const nextKey = useRef(0);
  const toRows = (questions: string[]): GuideRow[] =>
    questions.map((text) => ({ key: `q${nextKey.current++}`, text }));

  useEffect(() => {
    let cancelled = false;
    getMyAssistantGuide()
      .then((guide) => {
        if (cancelled) return;
        if (!guide) {
          setUnavailable(true);
          return;
        }
        setRows(toRows(guide.questions));
        setIsDefault(guide.isDefault);
      })
      .catch(() => {
        if (!cancelled) message.error(translate(t, TRANSLATIONS.ASSISTANT_GUIDE_LOAD_ERROR));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateRow = (key: string, text: string) => {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, text } : row)));
    setDirty(true);
  };

  const removeRow = (key: string) => {
    setRows((current) => current.filter((row) => row.key !== key));
    setDirty(true);
  };

  const addRow = () => {
    setRows((current) => [...current, { key: `q${nextKey.current++}`, text: '' }]);
    setDirty(true);
  };

  const handleSave = async () => {
    // Blank rows are somebody who pressed "add" and changed their mind, not an
    // answer. An empty list after that IS an answer, and the server stores it.
    const questions = rows.map((row) => row.text.trim()).filter((text) => text.length > 0);

    setSaving(true);
    try {
      const saved = await updateMyAssistantGuide(questions);
      setRows(toRows(saved.questions));
      setIsDefault(saved.isDefault);
      setDirty(false);
      message.success(translate(t, TRANSLATIONS.ASSISTANT_GUIDE_SAVED));
    } catch {
      message.error(translate(t, TRANSLATIONS.ASSISTANT_GUIDE_SAVE_ERROR));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '28px 0' }}>
        <Spin />
      </div>
    );
  }

  if (unavailable) return null;

  return (
    <div style={{ maxWidth: 560 }}>
      <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: isDefault ? 12 : 16 }}>
        {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_INTRO)}
      </Paragraph>

      {isDefault && (
        <div
          style={{
            background: TEAL_SOFT_BG,
            color: TEAL,
            borderRadius: 14,
            padding: '10px 14px',
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_DEFAULT_NOTICE)}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((row) => (
          <div key={row.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Input
              value={row.text}
              maxLength={ASSISTANT_GUIDE_MAX_QUESTION_LENGTH}
              placeholder={translate(t, TRANSLATIONS.ASSISTANT_GUIDE_PLACEHOLDER)}
              onChange={(event) => updateRow(row.key, event.target.value)}
              style={{ borderRadius: 12 }}
            />
            <Button
              danger
              type="text"
              icon={<IconTrash size={16} />}
              aria-label={translate(t, TRANSLATIONS.ASSISTANT_GUIDE_REMOVE)}
              title={translate(t, TRANSLATIONS.ASSISTANT_GUIDE_REMOVE)}
              onClick={() => removeRow(row.key)}
            />
          </div>
        ))}
      </div>

      {rows.length === 0 && (
        <Text type="secondary" style={{ fontSize: 13, display: 'block' }}>
          {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_EMPTY_HINT)}
        </Text>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 16 }}>
        <Button
          shape="round"
          icon={<IconPlus size={16} />}
          disabled={rows.length >= ASSISTANT_GUIDE_MAX_QUESTIONS}
          onClick={addRow}
        >
          {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_ADD)}
        </Button>
        <Button type="primary" shape="round" loading={saving} disabled={!dirty} onClick={handleSave}>
          {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_SAVE)}
        </Button>
      </div>

      {rows.length >= ASSISTANT_GUIDE_MAX_QUESTIONS && (
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
          {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_MAX, { max: ASSISTANT_GUIDE_MAX_QUESTIONS })}
        </Text>
      )}

      {/* Said out loud rather than left to be discovered: a professional who
          expects all of these to be asked, every time, would be right to feel
          lied to when three come back. */}
      <Text
        type="secondary"
        style={{ fontSize: 12, display: 'block', marginTop: 14, color: token.colorTextTertiary }}
      >
        {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_CAP_NOTE)}
      </Text>

      {/* Their price list steers the questions too, and nobody would ever
          guess that from this screen. The second half of the sentence is the
          important half: the first thing a professional thinks when told their
          rate card feeds a customer-facing assistant is that their prices are
          about to leak. They are not -- no figure is ever sent. */}
      <Text
        type="secondary"
        style={{ fontSize: 12, display: 'block', marginTop: 8, color: token.colorTextTertiary }}
      >
        {translate(t, TRANSLATIONS.ASSISTANT_GUIDE_RATE_NOTE)}
      </Text>
    </div>
  );
};

export default AssistantGuideEditor;
